import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, ChevronDown, ChevronUp, Shield } from 'lucide-react';
import { useLang } from '@/hooks/useLang';

interface ConsentState {
  necessary: boolean;  // always true
  analytics: boolean;
  marketing: boolean;
  functional: boolean;
}

const STORAGE_KEY = 'interp_cookie_consent';

function loadConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveConsent(state: ConsentState) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

export default function GDPRConsent() {
  const { t } = useLang();
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [consent, setConsent] = useState<ConsentState>({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    const existing = loadConsent();
    if (!existing) {
      setTimeout(() => setVisible(true), 800);
    }
  }, []);

  const acceptAll = () => {
    const full: ConsentState = { necessary: true, analytics: true, marketing: true, functional: true };
    saveConsent(full);
    setVisible(false);
  };

  const rejectAll = () => {
    const min: ConsentState = { necessary: true, analytics: false, marketing: false, functional: false };
    saveConsent(min);
    setVisible(false);
  };

  const saveCustom = () => {
    saveConsent({ ...consent, necessary: true });
    setVisible(false);
  };

  const toggle = (key: keyof ConsentState) => {
    if (key === 'necessary') return;
    setConsent(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const CATEGORIES = [
    {
      key: 'necessary' as const,
      labelRo: 'Strict Necesare',
      labelEn: 'Strictly Necessary',
      descRo: 'Esențiale pentru funcționarea site-ului. Nu pot fi dezactivate.',
      descEn: 'Essential for site operation. Cannot be disabled.',
      locked: true,
    },
    {
      key: 'functional' as const,
      labelRo: 'Funcționale',
      labelEn: 'Functional',
      descRo: 'Memorează preferințele dvs. (limbă, regiune). Îmbunătățesc experiența.',
      descEn: 'Remember your preferences (language, region). Improve experience.',
      locked: false,
    },
    {
      key: 'analytics' as const,
      labelRo: 'Analitice',
      labelEn: 'Analytics',
      descRo: 'Statistici anonime despre utilizarea site-ului (Google Analytics).',
      descEn: 'Anonymous usage statistics (Google Analytics).',
      locked: false,
    },
    {
      key: 'marketing' as const,
      labelRo: 'Marketing',
      labelEn: 'Marketing',
      descRo: 'Publicitate personalizată și urmărire conversii (Meta, LinkedIn).',
      descEn: 'Personalized advertising and conversion tracking (Meta, LinkedIn).',
      locked: false,
    },
  ];

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: 'spring', stiffness: 260, damping: 28 }}
          className="fixed bottom-4 left-4 md:left-6 md:right-auto md:max-w-sm z-[200]"
        >
          <div className="rounded-2xl overflow-hidden" style={{ background: '#0d1120', border: '1px solid rgba(74,184,255,0.22)', boxShadow: '0 20px 60px rgba(0,0,0,0.65), 0 0 0 1px rgba(74,184,255,0.08)' }}>
            {/* Header */}
            <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-3">
              <div className="flex items-center gap-2">
                <Cookie size={16} style={{ color: '#4ab8ff' }} />
                <span className="text-sm font-semibold text-white">{t('Preferințe Cookie-uri', 'Cookie Preferences')}</span>
              </div>
              <button onClick={rejectAll} className="text-white/25 hover:text-white/55 transition-colors mt-0.5"><X size={14}/></button>
            </div>

            {/* Body */}
            <div className="px-5 pb-3">
              <p className="text-xs text-white/42 leading-relaxed mb-3">
                {t(
                  'Utilizăm cookie-uri pentru a îmbunătăți experiența dvs. Puteți alege ce categorii acceptați. Cookie-urile strict necesare sunt întotdeauna active.',
                  'We use cookies to improve your experience. You can choose which categories to accept. Strictly necessary cookies are always active.'
                )}
              </p>

              {/* Expandable details */}
              <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1.5 text-[11px] text-[#4ab8ff] hover:text-white transition-colors mb-2">
                {expanded ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
                {t('Personalizează opțiunile', 'Customize options')}
              </button>

              <AnimatePresence>
                {expanded && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="space-y-2 py-2">
                      {CATEGORIES.map(cat => (
                        <div key={cat.key} className="flex items-start gap-3 p-2.5 rounded-lg" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)' }}>
                          <div className="flex-1 min-w-0">
                            <div className="text-[11px] font-semibold text-white/80">{t(cat.labelRo, cat.labelEn)}</div>
                            <div className="text-[10px] text-white/35 leading-relaxed mt-0.5">{t(cat.descRo, cat.descEn)}</div>
                          </div>
                          {/* Toggle */}
                          <button
                            onClick={() => toggle(cat.key)}
                            disabled={cat.locked}
                            className="shrink-0 mt-0.5 w-9 h-5 rounded-full transition-all relative"
                            style={{
                              background: consent[cat.key] ? 'rgba(74,184,255,0.85)' : 'rgba(255,255,255,0.12)',
                              cursor: cat.locked ? 'not-allowed' : 'pointer',
                            }}
                          >
                            <span className="absolute top-0.5 transition-all duration-200 w-4 h-4 rounded-full bg-white" style={{ left: consent[cat.key] ? 'calc(100% - 18px)' : '2px' }}/>
                          </button>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* GDPR notice */}
            <div className="px-5 pb-3 flex items-center gap-1.5">
              <Shield size={10} style={{ color: '#4ab8ff' }} />
              <span className="text-[9px] text-white/22 tracking-wide">{t('Conform RGPD (UE) 2016/679 · Directiva ePrivacy', 'Compliant with GDPR (EU) 2016/679 · ePrivacy Directive')}</span>
            </div>

            {/* Buttons */}
            <div className="px-5 pb-5 flex flex-col gap-2">
              <button onClick={acceptAll} className="w-full py-2.5 rounded text-xs font-semibold btn-gradient">
                {t('Acceptă toate', 'Accept all')}
              </button>
              <div className="flex gap-2">
                {expanded && (
                  <button onClick={saveCustom} className="flex-1 py-2.5 rounded text-xs font-semibold btn-outline-gradient">
                    {t('Salvează selecția', 'Save selection')}
                  </button>
                )}
                <button onClick={rejectAll} className="flex-1 py-2.5 rounded text-xs text-white/35 hover:text-white/60 transition-colors" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
                  {t('Respinge toate', 'Reject all')}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
